import React, { useEffect, useState } from "react";
import LoadingComponent from "@/components/common/LoadingComponent";
import ErrorState from "@/components/common/ErrorStateComponent";
import ParkingChartComponent from "@/components/BoardPack/components/charts/ParkingChartComponent";

const COLORS = {
  chartBlueLight: "#589CDB",
  chartOrangeLi: "#EF7521",
  chartGray: "#9A9A9A",
  tableBlueLight: "#9BC2E6",
  tableGrayDark: "#404040",
  navyDark: "#002060",
  grayBg: "#D9D9D9",
  darkGray: "#4A4A4A",
  white: "#FFFFFF",
  black: "#000000",
  lightBorder: "#CCCCCC",
};

const STATIC_RESPONSE = {
  success: true,
  meta: {
    contract: "0020016",
    year: 2025,
    years: [2023, 2024, 2025],
    month: "Dec",
    value_type: 1,
  },
  data: {
    table: {
      Ticket_Count: {
        "2023": {
          "0.10": "34,288", "0.15": "24,526", "0.20": "25,403", "0.30": "51,412",
          "0.40": "48,571", "0.50": "43,270", "1": "45,303", "2": "100,144",
          "3": "27,411", "4": "12,484", "5": "9,216", "6": "8,482",
          "7": "10,417", "8": "15,776", "9": "45,511", "10": "30,857",
          "11": "8,639", "12": "4,002", "13": "5,692", "14": "2,522",
          "15": "1,189", "16": "1,054", "17": "927", "18": "684",
          "19": "529", "20": "437", "21": "342", "22": "300",
          "23": "280", "24": "411", "48": "5,508", "72": "2,830", "96": "2,197",
        },
        "2024": {
          "0.10": "41,642", "0.15": "27,849", "0.20": "26,957", "0.30": "53,212",
          "0.40": "49,566", "0.50": "42,873", "1": "42,024", "2": "92,528",
          "3": "24,518", "4": "11,818", "5": "9,531", "6": "8,776",
          "7": "10,435", "8": "15,834", "9": "51,177", "10": "31,535",
          "11": "9,413", "12": "6,087", "13": "8,424", "14": "3,854",
          "15": "1,398", "16": "1,046", "17": "831", "18": "655",
          "19": "504", "20": "451", "21": "368", "22": "293",
          "23": "311", "24": "329", "48": "5,180", "72": "2,803", "96": "2,505",
        },
        "2025": {
          "0.10": "39,901", "0.15": "28,228", "0.20": "26,988", "0.30": "54,592",
          "0.40": "51,953", "0.50": "45,791", "1": "45,048", "2": "102,872",
          "3": "27,944", "4": "12,904", "5": "9,829", "6": "9,061",
          "7": "10,759", "8": "16,962", "9": "52,476", "10": "32,345",
          "11": "11,457", "12": "6,741", "13": "10,902", "14": "5,247",
          "15": "1,569", "16": "1,110", "17": "934", "18": "753",
          "19": "591", "20": "512", "21": "415", "22": "422",
          "23": "352", "24": "333", "48": "6,357", "72": "3,381", "96": "2,979",
        },
      },
    },
    top5: {
      "2023": [
        { duration: "2", value: "100,144" },
        { duration: "0.30", value: "51,412" },
        { duration: "0.40", value: "48,571" },
        { duration: "9", value: "45,511" },
        { duration: "1", value: "45,303" },
      ],
      "2024": [
        { duration: "2", value: "92,528" },
        { duration: "0.30", value: "53,212" },
        { duration: "9", value: "51,177" },
        { duration: "0.40", value: "49,566" },
        { duration: "0.50", value: "42,873" },
      ],
      "2025": [
        { duration: "2", value: "102,872" },
        { duration: "0.30", value: "54,592" },
        { duration: "9", value: "52,476" },
        { duration: "0.40", value: "51,953" },
        { duration: "0.50", value: "45,791" },
      ],
    },
  },
};

const cleanVal = (val) => (!val || val === "-") ? "0" : val;

export default function KpiChart14({ year = "", month = "", monthNumber = "", contract = "", page = null }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [top5Data, setTop5Data] = useState([]);
  const [bottomTableData, setBottomTableData] = useState([]);
  const [years, setYears] = useState([]);

  const processData = () => {
    try {
      setLoading(true);
      setError(null);

      const response = STATIC_RESPONSE;

      if (response?.success && response?.data) {
        const apiYears = response.meta?.years || [];
        setYears(apiYears);

        const tableData = response.data.table["Ticket_Count"];
        const firstYear = apiYears[0]?.toString();
        const durations = Object.keys(tableData[firstYear] || {});
        const sortedDurations = [...durations].sort((a, b) => parseFloat(a) - parseFloat(b));

        // Chart data - REVERSED for bottom-to-top display
        const processedChartData = sortedDurations
          .map((duration) => {
            const dataPoint = { duration };
            apiYears.forEach((yr) => {
              const yearKey = yr.toString();
              dataPoint[`y${yr}`] = parseInt(
                cleanVal(tableData[yearKey]?.[duration]).replace(/,/g, "")
              );
            });
            return dataPoint;
          })
          .reverse();

        // Top5 data
        const processedTop5 = [];
        const maxLength = Math.max(
          ...apiYears.map((yr) => response.data.top5[yr.toString()]?.length || 0)
        );
        for (let i = 0; i < maxLength; i++) {
          const row = {};
          apiYears.forEach((yr) => {
            const yearKey = yr.toString();
            row[`time${yr}`] = response.data.top5[yearKey]?.[i]?.duration || "";
            row[`value${yr}`] = cleanVal(response.data.top5[yearKey]?.[i]?.value);
          });
          processedTop5.push(row);
        }

        // Bottom table data - NO REVERSE
        const processedBottomTable = sortedDurations.map((duration) => {
          const row = { duration };
          apiYears.forEach((yr) => {
            const yearKey = yr.toString();
            row[`y${yr}`] = cleanVal(tableData[yearKey]?.[duration]);
          });
          return row;
        });

        setChartData(processedChartData);
        setTop5Data(processedTop5);
        setBottomTableData(processedBottomTable);
      } else {
        setError("No data available");
      }
    } catch (err) {
      console.error("Error processing parking data:", err);
      setError(err.message || "Failed to process parking data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    processData();
  }, []);

  if (loading) return <LoadingComponent />;
  if (error) return <ErrorState error={error} onRetry={processData} />;

  return (
    <ParkingChartComponent
      chartData={chartData}
      top5Data={top5Data}
      bottomTableData={bottomTableData}
      years={years}
      colors={COLORS}
      yAxisLabel={page?.yAxisLabel}
      mainTitle={page?.title}
      subTitle={page?.subtitle}
      chartTitle={page?.in_chart_content_line_1}
      chartSubTitle={page?.in_chart_content_line_2}
      bottomLabel={page?.bottomLabel}
    />
  );
}